/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CommunicationListItem, SalesManagementReportDialogConfig } from '@gh/builder-shared';
import {
  ChecklistItemDto,
  CommunicationType,
  CommunityDto,
  ContactCustomerType,
  ContactDetailsShortDto,
  LeadDetailsInitDto,
  LeadDetailsSaveDto,
  LeadListItemDto,
  LetterDto,
  LetterFinalVersionDto,
  QuotationDto,
  QuotationRfqNumberDto,
  SalesFolderCompleteDto,
  SalesFolderDto,
  SalesFolderRfqGeneralDto,
  SalesFolderShortDto,
  SalesFolderStatus,
  SalesManagementCalculationType,
  SalesManagementReportFilterDto,
  SalesManagementReportHistoryItemDto,
  SfMeetingDto,
  UserDetailsShortDto,
} from '@gh/builder-shared-data';
import {
  DateTimeTypes,
  entityValidatorByEntityConfig,
  EntityValidatorFn,
  isDirtyTypeNew,
  TextTypes,
} from '@gh/common-shared';
import { AutocompleteItem, DirtyType, EntityVersion, ID, WithDirtyType, WithVersion } from '@gh/common-shared-data';
import { currentDate, isNotNil } from '@gh/core-util';

import { filter, find, head, map, omit, orderBy, pick, split, toNumber, uniqueId } from 'lodash';
import { SiteListItem } from './quote.model';

export type LeadListItem = LeadListItemDto;
export type LeadSalesFolderLetter = LetterDto;
export type LeadSalesFolderFinalVersionLetter = LetterFinalVersionDto;
export type LeadCommunity = CommunityDto;
export type SalesManagementReportHistoryItem = SalesManagementReportHistoryItemDto & { monthCode: string };
export type SalesManagementReportFilter = SalesManagementReportFilterDto & {
  monthCode: string;
  months: SalesManagementReportHistoryItem[];
  calculation: SalesManagementCalculationType;
};
export type SalesManagementReportFilterMonthSelectOption = {
  monthCode: string;
  name: string;
};

export const salesManagementReportFilterToDto = (reportFilter: SalesManagementReportFilter): SalesManagementReportFilterDto => {
  const { forMonth, forYear } = decomposeMonthCode(reportFilter.monthCode);
  return {
    ...<SalesManagementReportFilterDto>omit(reportFilter, ['monthCode', 'calculation']),
    forMonth,
    forYear,
    calculateNewFigures: reportFilter.calculation === SalesManagementCalculationType.CalculateNewFigures,
    recalculateFigures: reportFilter.calculation === SalesManagementCalculationType.RecalculateFigures,
    useExistingFigures: reportFilter.calculation === SalesManagementCalculationType.UseExistingFigures,
    months: map(reportFilter.months, (month: SalesManagementReportHistoryItem) => {
      const { forMonth: forHistoryMonth, forYear: forHistoryYear } = decomposeMonthCode(month.monthCode);
      return {
        ...<SalesManagementReportHistoryItemDto>omit(month, ['monthCode']),
        forMonth: forHistoryMonth,
        forYear: forHistoryYear,
      };
    }),
  };
};

const calculationValue = ({ forMonth, forYear, months }: SalesManagementReportFilterDto): SalesManagementCalculationType => {
  const month = <SalesManagementReportHistoryItem>find(months, { forMonth, forYear });
  const lastHistoryMonth = head(filter(months, { isFromHistory: true }));
  return month && !month.isFromHistory
    ? SalesManagementCalculationType.UseExistingFigures
    : month && month.isFromHistory && lastHistoryMonth && lastHistoryMonth.forYear === forYear && lastHistoryMonth.forMonth === forMonth
      ? SalesManagementCalculationType.RecalculateFigures
      : SalesManagementCalculationType.CalculateNewFigures;
};

export const salesManagementReportFilterFromDto = (reportFilter: SalesManagementReportFilterDto): SalesManagementReportFilter => ({
  ...reportFilter,
  monthCode: composeMonthCode(reportFilter),
  calculation: calculationValue(reportFilter),
  months: map(reportFilter.months, (month: SalesManagementReportHistoryItemDto) =>
    ({ ...month, monthCode: composeMonthCode(month) })),
});

// tslint:disable-next-line:max-line-length
export function composeMonthCode({ forYear, forMonth }: SalesManagementReportFilterDto | SalesManagementReportHistoryItemDto | SalesManagementReportDialogConfig): string {
  return `${forMonth}:${forYear}`;
}

export type SalesManagementReportFilterDate = {
  forMonth: number;
  forYear: number;
};

export function decomposeMonthCode(monthCode: string): SalesManagementReportFilterDate {
  const [forMonth, forYear] = split(monthCode, ':', 2); // tslint:disable-line:no-magic-numbers
  return {
    forMonth: toNumber(forMonth),
    forYear: toNumber(forYear),
  };
}

export type LeadContactDetails = {
  contactId: ID;
  name: string;
  address: string;
  phone: string;
  email: string;
  community: LeadCommunity;
  referredBy: any;
  details: string;
  version: EntityVersion;
};
export type LeadSalesFolderComplete = SalesFolderDto;
export type LeadSalesFolderMeeting = SfMeetingDto;
export type RfqGeneral = SalesFolderRfqGeneralDto;

export type LeadSfJobDetails = {
  jobNumber: number;
  baseDesign: AutocompleteItem;
  depositDate: Date;
  contractDate: Date;
  completedByName: string;
  memo: string;
  isDpp: boolean;
};

export type LeadSalesFolder = WithDirtyType<{
  salesFolderId: ID;
  earliestActivityId?: ID;
  name: string;
  created: Date;
  followUp?: Date;
  priority?: number;
  status: SalesFolderStatus;
  notProceedingReason?: string | number;
  stage?: number;
  memo: string;
  jobDetails?: LeadSfJobDetails;
  version?: EntityVersion;
}>;

export type QuoteSalesFolder = {
  name: string;
  priority?: number;
};

export type ContactDetailsShort = ContactDetailsShortDto;

export type LeadSalesFolderSave = WithDirtyType<{
  salesFolderId?: ID;
  name: string;
  priority: number;
  status: number | string;
  notProceedingReason?: number | string;
  stage: string;
  followUp: Date;
  version: EntityVersion;
}>;

export type LeadDetailsSave = WithDirtyType<WithVersion<{
  customer: LeadContactDetails;
  salesPerson: UserDetailsShort;
  customerType: ContactCustomerType;
  salesFolder?: LeadSalesFolderSave;
}>>;

export type LeadDetailsInit = WithVersion<{
  name: string;
  email?: string;
  customer: LeadContactDetails;
  salesPerson: UserDetailsShort;
  customerType: ContactCustomerType;
  communications: CommunicationListItem[];
  salesFolders: LeadSalesFolder[];
  promptPrint: boolean;
  isDppJobAllowed: boolean;
  isUnsubscribed: boolean;
  gDriveUrl: string;
  gDriveFolderId: string;
  typeOfCustomer: string;
  salespersonId: string;
  activeSalesFolderId?: ID;
}>;
export type LeadDetailsSaveResponse = LeadDetailsInit & { currentSalesFolder?: LeadSalesFolderComplete };

export type UserDetailsShort = UserDetailsShortDto;

export type LeadMasterInfo = {
  customer?: LeadContactDetails;
  salesPerson: UserDetailsShort;
  customerType: ContactCustomerType;
};

export type LeadApprovedQuote = QuotationRfqNumberDto;

export type SfCompletionQuoteListItem = QuotationDto;
export type SfCompletionCheckListItem = ChecklistItemDto;
export type SfCompletionRequest = SalesFolderCompleteDto;
export type SfCompletionAuthorization = {
  code: string;
  description: string;
  baseDesign?: AutocompleteItem;
  contractPrice: number;
  memo: string;
};

export type JobDppDetails = {
  baseDesign: AutocompleteItem;
  site: SiteListItem;
  estimateCost: number;
  memo: string;
};

export const NONE_APPROVED_QUOTE: LeadApprovedQuote = <any>{};

export const isQuoteCanBeCompleted = (quote: SfCompletionQuoteListItem) =>
  isNotNil(quote.rfqNumber) && isNotNil(quote.approvedDate);

export const validateLeadMasterInfo = <EntityValidatorFn<LeadMasterInfo>>entityValidatorByEntityConfig({
  customer: { required: true },
  salesPerson: { required: true },
  customerType: { required: true },
});

export const validatesLeadSalesFolder = <EntityValidatorFn<LeadSalesFolder>>entityValidatorByEntityConfig({
  name: { required: true },
  priority: { required: true },
  status: { required: true },
  notProceedingReason: { required:  ({ status }: LeadSalesFolder) => status === SalesFolderStatus.WillNotComplete },
  stage: { required: true },
  followUp: { type: DateTimeTypes.ShortDate },
});

export const getLeadCommunicationId = (communication: CommunicationListItem) => communication.communicationId;
export const getLeadSalesFolderId = (sf: LeadSalesFolder) => sf.salesFolderId;

// tslint:disable-next-line:no-non-null-assertion
export const getLeadSalesFolderMeetingId = (meeting: LeadSalesFolderMeeting) => meeting.id!;

export const newLeadSalesFolder = () => <LeadSalesFolder>({
  salesFolderId: uniqueId('sf:'),
  name: '',
  created: currentDate(),
  status: SalesFolderStatus.Active,
  notProceedingReason: '',
  stage: 0, // Stage.New
  preloaded: true,
  dirtyType: DirtyType.New,
  memo: '',
});

export const leadSalesFolderFromShortDto = ({ salesFolderId, name, created }: SalesFolderShortDto) =>
  ({ salesFolderId, name, created });

export const leadDetailsInitFromDto = (dto: LeadDetailsInitDto) => <LeadDetailsInit>({
  name: dto.name,
  customerType: dto.typeOfCustomer,
  customer: dto.contactId && {
    contactId: dto.contactId,
    name: dto.name,
    address: dto.address,
    phone: dto.phone,
    referredBy: dto.referredBy,
    details: dto.details,
    community: dto.communityId && { id: dto.communityId, name: dto.communityName },
  },
  email: dto.email,
  salesPerson: dto.salespersonId && {
    userId: dto.salespersonId,
    userName: dto.salespersonName,
  },
  promptPrint: dto.promptPrint,
  isDppJobAllowed: dto.isDppJobAllowed,
  isUnsubscribed: dto.isUnsubscribed,
  activeSalesFolderId: dto.activeSalesFolderId,
  salesFolders: (dto.salesFolders || []).map(leadSalesFolderFromShortDto),
  communications: dto.communications || [],
  gDriveUrl: dto.gDriveUrl,
  gDriveFolderId: dto.gDriveFolderId,
  version: dto.version,
  typeOfCustomer: dto.typeOfCustomer,
  salespersonId: dto.salespersonId,
});

export const leadDetailsSaveToDto = (model: LeadDetailsSave) => {
  const { salesFolder } = model;

  return <LeadDetailsSaveDto>({
    contactId: model.customer.contactId,
    name: model.customer.name,
    salespersonId: model.salesPerson.userId,
    typeOfCustomer: model.customerType,
    dirtyType: model.dirtyType,
    salesFolder: salesFolder && isDirtyTypeNew(salesFolder.dirtyType)
      ? { ...salesFolder, createdLocal: currentDate() } : salesFolder,
    version: model.version,
  });
};

export const mergeSalesFolder = (salesFolder: LeadSalesFolder,
                                 {
                                   priority, followUp, status, stage, meetings, memo,
                                   jobNumber, jobBaseDesignId, jobBaseDesignName, notProceedingReason, jobDepositDate, jobContractDate,
                                   completedByName, jobMemo, isDppJob, earliestActivityId,
                                 }: LeadSalesFolderComplete) => ({
  ...salesFolder,
  priority,
  followUp,
  status,
  notProceedingReason,
  stage,
  meetings,
  memo,
  earliestActivityId,
  jobDetails: jobNumber ? <LeadSfJobDetails>{
    jobNumber,
    baseDesign: { id: jobBaseDesignId, name: jobBaseDesignName },
    depositDate: jobDepositDate,
    contractDate: jobContractDate,
    completedByName,
    memo: jobMemo,
    isDpp: isDppJob,
  } : void 0,
});

export const mergeLeadCommunicationIntoDetails = (details: LeadDetailsInit, communication: CommunicationListItem) =>
  ({ ...details, communications: [communication].concat(details.communications) });

export const sfCompletionAuthorizationFromQuote = (quote: SfCompletionQuoteListItem) =>
  <SfCompletionAuthorization>({
    code: quote.housePlanCode,
    description: quote.housePlanName,
    contractPrice: quote.rfqPrice2,
    memo: '',
    baseDesign: void 0,
  });

export const sortCompletionQuotes = (quotes: SfCompletionQuoteListItem[]) => orderBy(quotes, ['lastUpdated'], 'desc');

export const validateCompletionAuthorization = entityValidatorByEntityConfig({
  baseDesign: { required: true },
  description: { type: TextTypes.Text255 },
});

export const jobDppDetailsToCreateDto = (dpp: JobDppDetails) => ({
  ...pick(dpp, ['memo', 'estimateCost']),
  baseDesignId: dpp.baseDesign.id,
  siteId: dpp.site.id,
});

export const docLetterToCommunication = (letter: LeadSalesFolderLetter, createdByName: string, contactId?: ID) =>
  ({
    contactId,
    communicationType: CommunicationType.Doc,
    message: '',
    subject: letter.name,
    communicationId: letter.communicationId,
    created: letter.created,
    attachments: [{
      googleFileId: letter.googleId,
      googleFileName: letter.name,
      mimeType: letter.mimeType,
      webViewLink: letter.link,
    }],
    createdByName,
  });
